'use client'

import React, { useActionState } from 'react'
import { submitContactForm, type ContactFormState } from './actions'

const requestTypes = [
  { value: 'info', label: "Demande d'information" },
  { value: 'reclamation', label: 'Réclamation' },
  { value: 'partenariat', label: 'Partenariat' },
  { value: 'recrutement', label: 'Recrutement / Candidature' },
  { value: 'presse', label: 'Presse & Médias' },
  { value: 'autre', label: 'Autre' },
]

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-text-main placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm'
const labelClass = 'block text-sm font-medium text-text-main mb-1.5'
const font = { fontFamily: 'Poppins, sans-serif' } as const

export default function ContactForm({ centres = [] }: { centres?: string[] }) {
  const [state, formAction, pending] = useActionState<ContactFormState, FormData>(
    submitContactForm,
    { success: false },
  )

  if (state.success) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h2 className="text-xl font-bold text-text-main mb-2" style={font}>Message envoyé !</h2>
        <p className="text-text-muted" style={font}>Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
      <h2 className="text-xl font-bold text-text-main mb-6" style={font}>Envoyez-nous un message</h2>

      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 text-sm text-red-700" style={font}>
          {state.error}
        </div>
      )}

      <form action={formAction} className="space-y-5" aria-label="Formulaire de contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="prenom" className={labelClass} style={font}>Prénom <span className="text-primary" aria-hidden="true">*</span></label>
            <input type="text" id="prenom" name="prenom" required placeholder="Marie" className={inputClass} style={font} />
          </div>
          <div>
            <label htmlFor="nom" className={labelClass} style={font}>Nom <span className="text-primary" aria-hidden="true">*</span></label>
            <input type="text" id="nom" name="nom" required placeholder="Dupont" className={inputClass} style={font} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="email" className={labelClass} style={font}>Email <span className="text-primary" aria-hidden="true">*</span></label>
            <input type="email" id="email" name="email" required placeholder="marie.dupont@email.com" className={inputClass} style={font} />
          </div>
          <div>
            <label htmlFor="telephone" className={labelClass} style={font}>Téléphone</label>
            <input type="tel" id="telephone" name="telephone" placeholder="+32 4XX XX XX XX" className={inputClass} style={font} />
          </div>
        </div>

        <div>
          <label htmlFor="type-demande" className={labelClass} style={font}>Type de demande <span className="text-primary" aria-hidden="true">*</span></label>
          <select id="type-demande" name="type-demande" required className={inputClass} style={font}>
            <option value="">Sélectionnez un type de demande</option>
            {requestTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="centre" className={labelClass} style={font}>Centre concerné</label>
          <select id="centre" name="centre" className={inputClass} style={font}>
            <option value="">Tous les centres / Non spécifié</option>
            {centres.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="message" className={labelClass} style={font}>Message <span className="text-primary" aria-hidden="true">*</span></label>
          <textarea id="message" name="message" required rows={5} placeholder="Décrivez votre demande..." className={`${inputClass} resize-none`} style={font} />
        </div>

        <div className="flex items-start gap-3">
          <input type="checkbox" id="rgpd" name="rgpd" required className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30 flex-shrink-0" />
          <label htmlFor="rgpd" className="text-sm text-text-muted leading-relaxed" style={font}>
            J&apos;accepte que mes données soient traitées conformément à la politique de confidentialité de CMMC CDP. <span className="text-primary" aria-hidden="true">*</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={pending}
          className="w-full bg-primary text-white font-semibold py-3.5 rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60"
          style={font}
        >
          {pending ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Envoi en cours…
            </>
          ) : (
            <>
              Envoyer le message
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
            </>
          )}
        </button>
      </form>
    </div>
  )
}
